import { useEffect } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import { useSelector } from "react-redux";

import SoftBox from "components/SoftBox";

import { useSoftUIController, setLayout } from "context";

import { Toaster } from "react-hot-toast";
import { afmsDashboardLayoutSx, isAfmsPath } from "afms/theme/commandCenterTheme";
import {
  pickSidebarVariant,
  selectUserDetails,
  SIDEBAR_VARIANTS,
} from "config/appMode";

// Horizontal gutter between a page's content and the portal sidebar / viewport
// edge. Set once because it is applied in two independent branches below: the
// portal-shell branch and the legacy fall-through branch a session lands in
// when variant detection does not tag it. Applies to every page rendered
// through DashboardLayout, in every portal.
const EDGE_TO_EDGE_GUTTER = "5px";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav } = controller;
  const { pathname } = useLocation();
  // "In AFMS shell" = either the path is /afms/* OR the user's resolved
  // sidebar variant is the AFMS green-leaf EnvironmentalPortalShell (or its
  // LCP cousin). The variant signal catches cross-portal pages like
  // /assets/add and /assets/edit which are mounted as legacy flex2
  // pages from the AFMS Settings nav.
  const layoutUser = useSelector((state) => state.auth.user);
  // Stale VENDOR_PORTAL reference here used to silently resolve to
  // undefined after the Tier 1 EnvironmentalPortal rename — every
  // cioccaauto page fell through to the legacy branch and rendered with
  // the 274px marginLeft floating the Card mid-viewport. Now tests all
  // three portal variants explicitly.
  const isAfmsShell = (() => {
    const v = pickSidebarVariant(selectUserDetails(layoutUser));
    return (
      v === SIDEBAR_VARIANTS.ENVIRONMENTAL_PORTAL ||
      v === SIDEBAR_VARIANTS.LCP_PORTAL ||
      v === SIDEBAR_VARIANTS.ENERGY_PORTAL
    );
  })();
  const isAfms = isAfmsPath(pathname) || isAfmsShell;
  // FixedOps portal shell (FixedOpsPortalShell) also places its sidebar as a
  // flex sibling with a flex:1 content area — so like the AFMS/LCP/Energy
  // shells, the legacy 274px marginLeft double-counts the sidebar and floats
  // the Card mid-viewport. Pages rendered via DashboardLayout inside the
  // FixedOps shell (e.g. /user/quotations/listing reached from the FixedOps
  // "Quotations" nav) must go edge-to-edge with the 20px gutter too.
  const isFixedOpsShell =
    pickSidebarVariant(selectUserDetails(layoutUser)) ===
    SIDEBAR_VARIANTS.FIXED_OPS_PORTAL;
  // Pages that should render their inner Card edge-to-edge from the
  // sidebar to the viewport edge: AFMS Settings (rebrand wrappers around
  // legacy listings) PLUS the legacy listing screens reached FROM those
  // settings pages — asset add/edit, ticket create/view/edit — when shown
  // inside the AFMS shell. Command Center / ECC keep their AFMS padding
  // because they rely on it for their internal grids.
  // Pure path-based edge-to-edge: any LCP-shell page should land
  // flush against the LcpPortalShell sidebar regardless of whether
  // the variant detector marked the session as LCP_PORTAL. If the
  // session falls through to the legacy main shell, the legacy
  // branch below also zeroes its marginLeft/padding for these paths.
  const isPathEdgeToEdge =
    pathname.startsWith("/lcp/")
    || pathname.startsWith("/energy/")
    || pathname.startsWith("/payables")
    || pathname.startsWith("/receivables")
    || pathname.startsWith("/pickup-invoices")
    || pathname.startsWith("/pickups")
    || pathname.startsWith("/vendor/pickups")
    || pathname.startsWith("/afms/approvals")
    // Asset add / edit pages (per the full-width-pages rule). Routed
    // here in the legacy "main" shell when the session isn't tagged as
    // an AFMS / LCP / Energy portal — without this gate the layout
    // forces `p:3` + a 274px marginLeft and the Card visibly floats in
    // the middle of the viewport.
    || pathname.startsWith("/assets/edit")
    || pathname.startsWith("/assets/add");
  // Global rule (saved as memory feedback_full_width_layout): when the
  // user is in a portal shell (EnvironmentalPortal / LCP / Energy), the
  // portal already supplies the sidebar as a flex sibling — so EVERY
  // page inside the shell goes edge-to-edge with the 20px gutter, no
  // per-path allowlist needed. The old allowlist kept missing paths
  // (e.g. /assets/listings) and floating their Cards in the middle of
  // the viewport with the legacy 274px marginLeft.
  const isAfmsEdgeToEdge =
    isAfms
    && (isAfmsShell
      || pathname.startsWith("/afms/settings")
      || pathname.startsWith("/pickup-invoices")
      || pathname.startsWith("/receivables")
      || pathname.startsWith("/payables")
      || pathname.startsWith("/pickups")
      || pathname.startsWith("/vendor/pickups")
      || pathname.startsWith("/lcp/"));

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [pathname]);

  return (
    <SoftBox
      className={isAfms ? "afms-dashboard-shell" : undefined}
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        ...(isAfms
          ? {
              ...afmsDashboardLayoutSx(
                { breakpoints, transitions, functions: { pxToRem } },
                miniSidenav
              ),
              // EnvironmentalPortalShell already places the AFMS sidebar as a flex
              // sibling, so the marginLeft from afmsDashboardLayoutSx is
              // double-counting the sidebar width — content ends up offset
              // ~280px to the right of where it should be. For the rebrand
              // Settings pages we want the card flush against the sidebar,
              // so zero both the padding AND the responsive marginLeft.
              // A small left gutter so the Card "extends to the side menu"
              // with a little visual separation rather than slamming flush
              // against the sidebar. See EDGE_TO_EDGE_GUTTER above. Existing
              // AFMS settings pages also pick this up — previous `p: 0` was
              // just as legal since their inner Card has its own padding.
              ...(isAfmsEdgeToEdge || isPathEdgeToEdge
                ? {
                    p: 0,
                    pl: EDGE_TO_EDGE_GUTTER,
                    pr: EDGE_TO_EDGE_GUTTER,
                    [breakpoints.up("xl")]: {
                      marginLeft: 0,
                    },
                  }
                : {}),
            }
          : {
              // Legacy main-shell branch. If the user's session falls
              // through to here (variant detection didn't mark them as
              // LCP_PORTAL) but the path is LCP-shell-owned, still
              // zero the marginLeft so the Card sits flush against the
              // shell's flex sibling sidebar instead of double-offset.
              ...(isPathEdgeToEdge || isFixedOpsShell
                ? { p: 0, pl: EDGE_TO_EDGE_GUTTER, pr: EDGE_TO_EDGE_GUTTER }
                : { p: 3 }),
              position: "relative",

              [breakpoints.up("xl")]: {
                marginLeft: isPathEdgeToEdge || isFixedOpsShell
                  ? 0
                  : miniSidenav
                    ? pxToRem(120)
                    : pxToRem(274),
                transition: transitions.create(["margin-left", "margin-right"], {
                  easing: transitions.easing.easeInOut,
                  duration: transitions.duration.standard,
                }),
              },
            }),
      })}
    >
      {children}
      <Toaster
        toastOptions={{
          // Default react-hot-toast error duration is 4s; bump to 10s
          // so the user can read what went wrong (Keycloak rejections,
          // 401s, etc.) instead of seeing it flash by on navigation.
          error: { duration: 10000 },
          success: { duration: 4000 },
        }}
      />
    </SoftBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
